import React, { useMemo, useCallback } from "react";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Input,
} from "reactstrap";

const DIHeaderBar = ({
  context,
  onFileNew,
  onFileExit,
  onViewResetLayout,
  onImportExcel,
  onToolsSettings,
  onHelpAbout,
  onCloseOp,
  onCloseJob,
  closeOpDisabled,
  closeJobDisabled,
}) => {
  const title = useMemo(() => {
    const mode =
      context?.inputOption === "1"
        ? "Job"
        : context?.inputOption === "2"
        ? "PO\\Line"
        : "";

    const id = context?.inputJob || "";
    const asm = context?.assembly ?? "";
    const part = context?.part ?? "";
    const wave = context?.waveNumber ? ` | Wave ${context.waveNumber}` : "";

    return `${mode}${mode && id ? ": " : ""}${id}${
      asm !== "" ? ` | ASM ${asm}` : ""
    }${part ? ` | Part ${part}` : ""}${wave}`;
  }, [
    context?.inputOption,
    context?.inputJob,
    context?.assembly,
    context?.part,
    context?.waveNumber,
  ]);

  const dnaDisplayValue = useMemo(
    () => context?.dnaDisplay || "",
    [context?.dnaDisplay]
  );

  const handleFileNew = useCallback(() => onFileNew?.(), [onFileNew]);
  const handleFileExit = useCallback(() => onFileExit?.(), [onFileExit]);
  const handleImportExcel = useCallback(() => onImportExcel?.(), [onImportExcel]);
  const handleCloseOp = useCallback(() => onCloseOp?.(), [onCloseOp]);
  const handleCloseJob = useCallback(() => onCloseJob?.(), [onCloseJob]);

  return (
    <div className="di-header-bar">
      <div className="di-header-inner">
        <div className="di-header-left">
          <UncontrolledDropdown className="di-menu-item">
            <DropdownToggle
              caret
              color="link"
              className="di-menu-toggle"
            >
              File
            </DropdownToggle>
            <DropdownMenu className="di-menu-dropdown">
              <DropdownItem onClick={handleFileNew}>New / Clear</DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={handleFileExit}>Exit</DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>

          <UncontrolledDropdown className="di-menu-item">
            <DropdownToggle
              caret
              color="link"
              className="di-menu-toggle"
            >
              Import
            </DropdownToggle>
            <DropdownMenu className="di-menu-dropdown">
              <DropdownItem onClick={handleImportExcel}>
                Vision Excel...
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>

          <UncontrolledDropdown className="di-menu-item">
            <DropdownToggle
              caret
              color="link"
              className="di-menu-toggle"
            >
              Close Op/Job
            </DropdownToggle>
            <DropdownMenu className="di-menu-dropdown">
              <DropdownItem onClick={handleCloseOp} disabled={!!closeOpDisabled}>
                Close Op
              </DropdownItem>
              <DropdownItem onClick={handleCloseJob} disabled={!!closeJobDisabled}>
                Close Job
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <div className="di-header-right">
          <div className="di-header-title" title={title}>
            {title}
          </div>

          <div className="di-dna-wrap">
            <span className="di-dna-label">DNA</span>
            <Input disabled value={dnaDisplayValue} className="di-dna-input" />
          </div>

          <Button
            size="sm"
            color="secondary"
            outline
            onClick={handleImportExcel}
            className="di-import-btn"
          >
            Import
          </Button>
        </div>
      </div>

      <style>{`
        .di-header-bar {
          border: 1px solid #e6e6e6;
          border-radius: 8px;
          background: #ffffff;
          width: 100%;
          min-width: 0;
          position: relative;
          z-index: 1200;
          overflow: visible !important;
        }

        .di-header-inner {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          min-width: 0;
          padding: 8px 12px;
          position: relative;
          overflow: visible !important;
        }

        .di-header-left {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 0 0 auto;
          min-width: 0;
          overflow: visible !important;
        }

        .di-menu-item {
          position: relative;
          flex: 0 0 auto;
        }

        .di-menu-toggle.btn.btn-link {
          text-decoration: none !important;
          color: #212529 !important;
          font-weight: 600;
          padding: 4px 8px !important;
          border: 0 !important;
          box-shadow: none !important;
          white-space: nowrap;
        }

        .di-menu-toggle.btn.btn-link:hover,
        .di-menu-toggle.btn.btn-link:focus,
        .di-menu-toggle.btn.btn-link:active,
        .di-menu-toggle.btn.btn-link.show {
          color: #0d6efd !important;
          text-decoration: none !important;
          box-shadow: none !important;
        }

        .di-menu-item .dropdown-menu,
        .di-menu-dropdown.dropdown-menu {
          position: absolute !important;
          inset: auto auto auto auto !important;
          top: calc(100% + 4px) !important;
          left: 0 !important;
          transform: none !important;
          z-index: 3000 !important;
          min-width: 180px;
          display: none;
        }

        .di-menu-item.show .dropdown-menu,
        .di-menu-item .dropdown-menu.show {
          display: block !important;
        }

        .di-header-right {
          margin-left: auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          flex: 1 1 auto;
          min-width: 0;
        }

        .di-header-title {
          flex: 1 1 auto;
          min-width: 0;
          max-width: 520px;
          font-size: 12px;
          color: #6c757d;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: right;
        }

        .di-dna-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 0 0 auto;
        }

        .di-dna-label {
          font-size: 12px;
          color: #6c757d;
          white-space: nowrap;
        }

        .di-dna-input {
          width: 90px;
          height: 30px;
          flex: 0 0 auto;
        }

        .di-import-btn {
          flex: 0 0 auto;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

export default React.memo(DIHeaderBar);